import React, { useEffect, useState } from "react";
import { Nfc, Copy, ExternalLink, Trash2, ShieldCheck, ShieldAlert, Smartphone, Globe, Printer, QrCode, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

// One tag per site — scanning its QR code or tapping its NFC chip opens that site's Live Monitor
// (docs/mobile/NFC_INTEGRATION.md §2.0). The QR code needs no NFC: many phones sold in India have
// none, and any camera reads it. Super admin creates/retires the tag here, prints the QR label, and
// optionally writes the same link onto an NFC chip; everyone with access to the site sees the tag
// and its visit log (QR scans and NFC taps are told apart).

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

// Printable label: QR code to scan, plus an NFC symbol to stick the chip behind (optional).
// 85 × 55 mm — business-card size; print on sticker paper or laminate it.
function printLabel(tag, siteName, qrSvg) {
  const w = window.open("", "_blank", "width=480,height=420");
  if (!w) { toast.error("Allow pop-ups to print the label"); return; }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>NFC label — ${esc(siteName)}</title>
<style>
  @page { size: 85mm 55mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; }
  .label { width: 85mm; height: 55mm; padding: 4mm 5mm; border: 0.3mm dashed #bbb; display: flex; flex-direction: column; justify-content: space-between; }
  .top { display: flex; align-items: center; justify-content: space-between; }
  .brand { font-weight: 800; font-size: 11pt; color: #ea580c; }
  .site { font-size: 8pt; color: #444; text-align: right; max-width: 45mm; }
  .tap { display: flex; align-items: center; gap: 4mm; }
  .qr { width: 30mm; height: 30mm; flex-shrink: 0; }
  .qr svg { width: 30mm; height: 30mm; display: block; }
  .ring { width: 11mm; height: 11mm; border: 0.8mm solid #ea580c; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; vertical-align: middle; margin-right: 1.5mm; }
  .ring svg { width: 6mm; height: 6mm; }
  .big { font-size: 13pt; font-weight: 800; color: #111; line-height: 1.1; }
  .nfc { font-size: 8pt; color: #333; margin-top: 2mm; display: flex; align-items: center; }
  .small { font-size: 7pt; color: #555; margin-top: 1.5mm; }
  .foot { font-size: 6.5pt; color: #777; display: flex; justify-content: space-between; }
  @media print { .label { border: none; } .hint { display: none; } }
  .hint { font-size: 9pt; color: #666; padding: 4mm 5mm; }
</style></head><body>
<div class="label">
  <div class="top"><div class="brand">Fire360Pro</div><div class="site">${esc(siteName)}</div></div>
  <div class="tap">
    <div class="qr">${qrSvg || ""}</div>
    <div>
      <div class="big">Scan to open<br/>live status</div>
      <div class="nfc"><span class="ring"><svg viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/><path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/><path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/><path d="M16.37 2a20.16 20.16 0 0 1 0 20"/></svg></span>or tap an NFC phone here</div>
      <div class="small">Phone camera → scan · authorised users only</div>
    </div>
  </div>
  <div class="foot"><span>${esc(tag.label)}</span><span>Tag ${esc(tag.tag_code)}</span></div>
</div>
<p class="hint">Print at 100% scale (no "fit to page"). If you also use an NFC chip, stick it behind the orange ring.</p>
<script>window.onload = () => setTimeout(() => window.print(), 200);</script>
</body></html>`);
  w.document.close();
}

export default function SiteNfcTag({ siteId, siteName, canManage }) {
  const [tag, setTag] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [label, setLabel] = useState("Main entrance");
  const [uid, setUid] = useState("");
  const [editUid, setEditUid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qrSvg, setQrSvg] = useState("");

  const load = () => {
    if (!siteId) return;
    Promise.all([
      api.get("/nfc/tags", { params: { site_id: siteId } }).then((r) => setTag((r.data || [])[0] || null)).catch(() => setTag(null)),
      api.get("/nfc/scans", { params: { site_id: siteId, limit: 8 } }).then((r) => setVisits(r.data || [])).catch(() => setVisits([])),
    ]).finally(() => setLoaded(true));
  };
  useEffect(() => {
    setLoaded(false);
    setEditUid(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // The QR code is drawn by the backend (/nfc/tags/{code}/qr.svg) — it needs the auth header, so
  // fetch it as text rather than pointing an <img> at the URL.
  useEffect(() => {
    if (!tag?.tag_code) { setQrSvg(""); return; }
    api.get(`/nfc/tags/${tag.tag_code}/qr.svg`, { responseType: "text" })
      .then((r) => setQrSvg(typeof r.data === "string" ? r.data : ""))
      .catch(() => setQrSvg(""));
  }, [tag?.tag_code]);

  const qrDataUrl = qrSvg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg)}` : "";

  // PNG for sharing (WhatsApp, email, a print shop): draw the SVG on a canvas with a white margin.
  const downloadQr = () => {
    if (!qrDataUrl) return;
    const img = new Image();
    img.onload = () => {
      const size = 1024, pad = 64;
      const c = document.createElement("canvas");
      c.width = size; c.height = size;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, size, size);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, pad, pad, size - 2 * pad, size - 2 * pad);
      c.toBlob((blob) => {
        if (!blob) { toast.error("Couldn't create the image"); return; }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `fire360pro-site-qr-${(siteName || tag.tag_code).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      }, "image/png");
    };
    img.onerror = () => toast.error("Couldn't create the image");
    img.src = qrDataUrl;
  };

  const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/nfc/tags/bind", { site_id: siteId, label, tag_uid: uid || null });
      toast.success("Tag created — write its link onto the chip");
      setUid("");
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const retire = async () => {
    if (!window.confirm("Retire this tag? Its link stops working immediately. You can create a new tag afterwards.")) return;
    try { await api.post(`/nfc/tags/${tag.tag_code}/retire`); toast.success("Tag retired"); load(); } catch (e) { err(e); }
  };

  const saveUid = async () => {
    try { await api.patch(`/nfc/tags/${tag.tag_code}`, { tag_uid: editUid }); toast.success("Chip serial saved"); setEditUid(null); load(); } catch (e) { err(e); }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed — select the link and copy it"));

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="site-nfc-tag">
      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2 flex items-center gap-1.5">
        <QrCode className="w-3.5 h-3.5 text-orange-600" strokeWidth={2.5} /> Site Tag — QR &amp; NFC
      </h4>

      {!loaded ? (
        <div className="text-[11px] text-gray-400">Loading…</div>
      ) : tag ? (
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-gray-900 truncate">{tag.label}</span>
            <span className="text-[10px] font-bold px-1.5 py-px rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700 uppercase">Active</span>
          </div>
          <div className="flex items-center gap-1">
            <code className="flex-1 min-w-0 truncate bg-white border border-gray-200 rounded px-2 py-1 text-[11px] text-gray-800" title={tag.url}>{tag.url}</code>
            <button onClick={() => copy(tag.url)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600" title="Copy link"><Copy className="w-3.5 h-3.5" /></button>
            <a href={`/t/${tag.tag_code}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-gray-200 text-gray-600" title="Test the link"><ExternalLink className="w-3.5 h-3.5" /></a>
          </div>
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-2" data-testid="site-tag-qr">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={`QR code for ${siteName || "this site"}`} className="w-28 h-28 shrink-0" style={{ imageRendering: "pixelated" }} />
            ) : (
              <div className="w-28 h-28 shrink-0 flex items-center justify-center text-[10px] text-gray-400 border border-dashed border-gray-200 rounded">QR loading…</div>
            )}
            <div className="space-y-1.5 min-w-0">
              <div className="text-[11px] text-gray-700 leading-snug">Scan with any phone camera to open this site's Live Monitor. No NFC needed.</div>
              <button onClick={downloadQr} disabled={!qrDataUrl} className="inline-flex items-center gap-1 text-[11px] text-gray-700 hover:text-orange-600 font-semibold disabled:opacity-40" data-testid="qr-download">
                <Download className="w-3 h-3" /> Download QR (PNG)
              </button>
            </div>
          </div>
          <div className="text-[10px] text-gray-500">
            Chip serial:{" "}
            {editUid === null ? (
              <>
                <span className="font-mono text-gray-700">{tag.tag_uid || "not recorded"}</span>
                {canManage && <button onClick={() => setEditUid(tag.tag_uid || "")} className="ml-1 text-orange-600 hover:underline">{tag.tag_uid ? "change" : "add"}</button>}
              </>
            ) : (
              <span className="inline-flex items-center gap-1 align-middle">
                <Input value={editUid} onChange={(e) => setEditUid(e.target.value)} placeholder="04:A1:B2:C3:D4:E5:F6" className="h-6 w-40 text-[10px] font-mono bg-white" />
                <button onClick={saveUid} className="text-orange-600 font-semibold hover:underline">Save</button>
                <button onClick={() => setEditUid(null)} className="text-gray-500 hover:underline">Cancel</button>
              </span>
            )}
          </div>
          <div className="text-[10px] text-gray-400">Created {fmt(tag.bound_at)} by {tag.bound_by}</div>
          <button onClick={() => printLabel(tag, siteName, qrSvg)} disabled={!qrSvg} className="inline-flex items-center gap-1 text-[11px] text-gray-700 hover:text-orange-600 font-semibold mr-3 disabled:opacity-40" data-testid="nfc-print-label">
            <Printer className="w-3 h-3" /> Print label (QR)
          </button>
          {canManage && (
            <button onClick={retire} className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700 font-semibold">
              <Trash2 className="w-3 h-3" /> Retire tag
            </button>
          )}
        </div>
      ) : canManage ? (
        <div className="space-y-2 text-xs">
          <p className="text-[11px] text-gray-500">No tag yet. Create one to get a QR code for this site — print its label for the panel. Optionally also write the same link onto an NFC sticker (NTAG213/215).</p>
          <div>
            <Label className="text-[10px]">Where it's fixed</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 text-xs bg-white mt-0.5" placeholder="Main entrance" />
          </div>
          <div>
            <Label className="text-[10px]">Chip serial (optional — can add later)</Label>
            <Input value={uid} onChange={(e) => setUid(e.target.value)} className="h-8 text-xs bg-white mt-0.5 font-mono" placeholder="04:A1:B2:C3:D4:E5:F6" />
          </div>
          <Button onClick={create} disabled={busy} size="sm" className="w-full h-8 text-xs bg-orange-600 hover:bg-orange-700 text-white" data-testid="nfc-create-btn">
            <Nfc className="w-3.5 h-3.5 mr-1" /> Create site tag
          </Button>
        </div>
      ) : (
        <div className="text-[11px] text-gray-500">No site tag (QR / NFC) at this site yet.</div>
      )}

      {loaded && visits.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Recent visits</div>
          <ul className="space-y-1">
            {visits.map((v) => (
              <li key={v.scan_id} className="flex items-center gap-1.5 text-[11px] text-gray-700">
                {v.source === "qr" ? <QrCode className="w-3 h-3 text-gray-400 shrink-0" title="QR scan" />
                  : v.source === "web" ? <Globe className="w-3 h-3 text-gray-400 shrink-0" title="NFC tap (browser)" />
                  : <Smartphone className="w-3 h-3 text-gray-400 shrink-0" title="NFC tap (app)" />}
                <span className="truncate flex-1">{v.user_name || v.user_email}</span>
                {v.uid_ok === true && <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" title="Chip verified" />}
                {v.uid_ok === false && <ShieldAlert className="w-3 h-3 text-red-600 shrink-0" title="Different chip — not verified" />}
                <span className="text-[10px] text-gray-400 shrink-0">{fmt(v.scanned_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
